import { FormEvent, useEffect, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Smartphone, Link2, XCircle, CheckCircle2, AlertTriangle, Trash2, PencilLine, ExternalLink, Settings2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog'
import { Label } from '@/components/ui/Label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { api } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import type { AppReleaseGroup } from '@/types'

function getErrorMessage(err: unknown, fallback = '操作失败，请稍后再试。'): string {
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    return (err as { message: string }).message
  }
  return fallback
}

interface PublishFormState {
  version: string
  buildNumber: string
  releaseNotes: string
}

interface DownloadConfigFormState {
  androidDownloadUrl: string
  iosDownloadUrl: string
}

interface EditDialogState {
  open: boolean
  release: AppReleaseGroup | null
  version: string
  buildNumber: string
  releaseNotes: string
}

interface DeleteDialogState {
  open: boolean
  release: AppReleaseGroup | null
}

export function AppReleasesPage() {
  const queryClient = useQueryClient()

  const [publishForm, setPublishForm] = useState<PublishFormState>({
    version: '',
    buildNumber: '',
    releaseNotes: '',
  })
  const [downloadConfigForm, setDownloadConfigForm] = useState<DownloadConfigFormState>({
    androidDownloadUrl: '',
    iosDownloadUrl: '',
  })
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'warning'; title: string; description?: string } | null>(null)
  const [editDialog, setEditDialog] = useState<EditDialogState>({
    open: false,
    release: null,
    version: '',
    buildNumber: '',
    releaseNotes: '',
  })
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    release: null,
  })

  const releasesQuery = useQuery({
    queryKey: ['app-releases'],
    queryFn: () => api.getAppReleases(50),
    select: (response) => response.releases,
  })

  const downloadConfigQuery = useQuery({
    queryKey: ['app-download-config'],
    queryFn: () => api.getAppDownloadConfig(),
    select: (response) => response.config,
  })

  useEffect(() => {
    if (!downloadConfigQuery.data) return
    setDownloadConfigForm({
      androidDownloadUrl: downloadConfigQuery.data.androidDownloadUrl ?? '',
      iosDownloadUrl: downloadConfigQuery.data.iosDownloadUrl ?? '',
    })
  }, [downloadConfigQuery.data])

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(null), 4000)
    return () => window.clearTimeout(timer)
  }, [toast])

  const showToast = (type: 'success' | 'error' | 'warning', title: string, description?: string) => {
    setToast({ type, title, description })
  }

  const toastThemes: Record<'success' | 'error' | 'warning', { container: string; icon: ReactNode }> = {
    success: {
      container: 'border-green-200 bg-green-50 text-green-800',
      icon: <CheckCircle2 className="h-5 w-5 text-green-600" />,
    },
    error: {
      container: 'border-red-200 bg-red-50 text-red-800',
      icon: <XCircle className="h-5 w-5 text-red-600" />,
    },
    warning: {
      container: 'border-amber-200 bg-amber-50 text-amber-800',
      icon: <AlertTriangle className="h-5 w-5 text-amber-600" />,
    },
  }

  const downloadConfigMutation = useMutation({
    mutationFn: (payload: DownloadConfigFormState) =>
      api.updateAppDownloadConfig({
        androidDownloadUrl: payload.androidDownloadUrl.trim() || null,
        iosDownloadUrl: payload.iosDownloadUrl.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-download-config'] })
      showToast('success', '下载地址已保存', '全局下载链接已更新，后续发布将自动使用。')
    },
    onError: (error: unknown) => {
      showToast('error', '保存失败', getErrorMessage(error))
    },
  })

  const createMutation = useMutation({
    mutationFn: (payload: PublishFormState) =>
      api.createAppRelease({
        version: payload.version.trim(),
        buildNumber: Number(payload.buildNumber.trim()),
        releaseNotes: payload.releaseNotes.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-releases'] })
      setPublishForm({
        version: '',
        buildNumber: '',
        releaseNotes: '',
      })
      showToast('success', '版本已发布', '客户端将检测到新版本并提示更新。')
    },
    onError: (error: unknown) => {
      showToast('error', '发布失败', getErrorMessage(error))
    },
  })

  const editMutation = useMutation({
    mutationFn: (payload: { buildNumber: number; version?: string; newBuildNumber?: number; releaseNotes?: string | null }) =>
      api.updateAppReleaseGroup(payload.buildNumber, {
        version: payload.version,
        buildNumber: payload.newBuildNumber,
        releaseNotes: payload.releaseNotes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-releases'] })
      showToast('success', '版本信息已更新')
      setEditDialog({
        open: false,
        release: null,
        version: '',
        buildNumber: '',
        releaseNotes: '',
      })
    },
    onError: (error: unknown) => {
      showToast('error', '更新失败', getErrorMessage(error, '更新失败，请稍后再试。'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (buildNumber: number) => api.deleteAppReleaseGroup(buildNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-releases'] })
      showToast('success', '已删除发布版本')
      setDeleteDialog({ open: false, release: null })
    },
    onError: (error: unknown) => {
      showToast('error', '删除失败', getErrorMessage(error, '删除失败，请稍后再试。'))
    },
  })

  const handleDownloadConfigSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!downloadConfigForm.androidDownloadUrl.trim() && !downloadConfigForm.iosDownloadUrl.trim()) {
      showToast('warning', '请至少填写一个下载地址')
      return
    }
    downloadConfigMutation.mutate(downloadConfigForm)
  }

  const handlePublishSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!publishForm.version.trim()) {
      showToast('warning', '请输入版本号')
      return
    }
    const buildNumber = Number(publishForm.buildNumber.trim())
    if (!Number.isFinite(buildNumber) || buildNumber <= 0) {
      showToast('warning', '请输入有效的构建号')
      return
    }
    if (!downloadConfigForm.androidDownloadUrl.trim() && !downloadConfigForm.iosDownloadUrl.trim()) {
      showToast('warning', '请先配置全局下载地址', '在上方填写 Android / iOS 下载链接并保存。')
      return
    }
    createMutation.mutate(publishForm)
  }

  const handleOpenEdit = (release: AppReleaseGroup) => {
    setEditDialog({
      open: true,
      release,
      version: release.version,
      buildNumber: String(release.buildNumber),
      releaseNotes: release.releaseNotes ?? '',
    })
  }

  const handleEditSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!editDialog.release) return

    const newBuildNumber = Number(editDialog.buildNumber.trim())
    if (!Number.isFinite(newBuildNumber) || newBuildNumber <= 0) {
      showToast('warning', '请输入有效的构建号')
      return
    }

    editMutation.mutate({
      buildNumber: editDialog.release.buildNumber,
      version: editDialog.version.trim(),
      newBuildNumber: newBuildNumber !== editDialog.release.buildNumber ? newBuildNumber : undefined,
      releaseNotes: editDialog.releaseNotes.trim() || null,
    })
  }

  const renderReleaseList = (releases: AppReleaseGroup[] | undefined) => {
    if (!releases?.length) {
      return <p className="text-sm text-gray-500">暂无发布记录</p>
    }

    return (
      <div className="space-y-3">
        {releases.map((release) => (
          <div
            key={release.buildNumber}
            className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  v{release.version} · Build {release.buildNumber}
                </p>
                {release.createdAt && (
                  <p className="text-xs text-gray-500">
                    发布时间：{formatDate(release.createdAt)}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenEdit(release)}
                >
                  <PencilLine className="h-4 w-4" />
                  <span className="sr-only">编辑版本</span>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => setDeleteDialog({ open: true, release })}
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">删除版本</span>
                </Button>
              </div>
            </div>
            {release.releaseNotes && (
              <p className="mt-3 whitespace-pre-line text-sm text-gray-600">
                更新说明：{release.releaseNotes}
              </p>
            )}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {toast && (
        <div
          className={`fixed top-6 right-6 z-50 w-full max-w-sm rounded-2xl border p-4 shadow-lg transition-all duration-200 ${toastThemes[toast.type].container}`}
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5">{toastThemes[toast.type].icon}</div>
            <div className="flex-1">
              <p className="text-sm font-semibold">{toast.title}</p>
              {toast.description && (
                <p className="mt-1 text-xs opacity-80">{toast.description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setToast(null)}
              aria-label="关闭提示"
              className="text-current"
            >
              <XCircle className="h-4 w-4 opacity-60 hover:opacity-100 transition-opacity" />
            </button>
          </div>
        </div>
      )}

      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-black/5" />
        <div className="relative space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-wider backdrop-blur-sm">
            <Smartphone className="h-4 w-4" /> 应用版本管理
          </div>
          <h1 className="text-3xl font-bold lg:text-4xl">管理员 · 客户端版本发布</h1>
          <p className="max-w-3xl text-sm text-white/90 lg:text-base">
            下载地址为全局配置，一般只需设置一次。每次发布只需填写版本号、构建号和更新说明。
          </p>
        </div>
      </div>

      <Card className="border-gray-100 shadow-md">
        <CardHeader className="border-b border-gray-100 bg-gradient-to-r from-slate-50 to-gray-50">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-slate-100 p-2">
              <Settings2 className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <CardTitle className="text-lg font-bold">全局下载地址</CardTitle>
              <p className="text-sm text-gray-600 mt-1">Android 蒲公英链接与 iOS 下载链接，客户端更新时将自动使用。</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          {downloadConfigQuery.isLoading ? (
            <p className="text-sm text-gray-500">正在加载...</p>
          ) : (
            <form className="space-y-4" onSubmit={handleDownloadConfigSubmit}>
              <div>
                <label className="text-sm font-medium text-gray-700">Android 下载地址（蒲公英）</label>
                <Input
                  value={downloadConfigForm.androidDownloadUrl}
                  placeholder="https://www.pgyer.com/xxx"
                  onChange={(event) =>
                    setDownloadConfigForm((prev) => ({ ...prev, androidDownloadUrl: event.target.value }))
                  }
                />
                {downloadConfigForm.androidDownloadUrl.trim() && (
                  <a
                    href={downloadConfigForm.androidDownloadUrl.trim()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                  >
                    <ExternalLink className="h-3 w-3" /> 预览链接
                  </a>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">iOS 下载地址</label>
                <Input
                  value={downloadConfigForm.iosDownloadUrl}
                  placeholder="https://testflight.apple.com/... 或 App Store 链接"
                  onChange={(event) =>
                    setDownloadConfigForm((prev) => ({ ...prev, iosDownloadUrl: event.target.value }))
                  }
                />
                {downloadConfigForm.iosDownloadUrl.trim() && (
                  <a
                    href={downloadConfigForm.iosDownloadUrl.trim()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                  >
                    <ExternalLink className="h-3 w-3" /> 预览链接
                  </a>
                )}
              </div>
              <Button type="submit" variant="secondary" className="w-full sm:w-auto" disabled={downloadConfigMutation.isPending}>
                {downloadConfigMutation.isPending ? '保存中...' : '保存下载地址'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      <Card className="border-gray-100 shadow-md">
        <CardHeader className="border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-2">
              <Link2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg font-bold">发布新版本</CardTitle>
              <p className="text-sm text-gray-600 mt-1">填写版本号、构建号和更新说明即可，无需重复填写下载地址。</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <form className="space-y-4" onSubmit={handlePublishSubmit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-gray-700">版本号</label>
                <Input
                  value={publishForm.version}
                  placeholder="例如 1.2.3"
                  onChange={(event) =>
                    setPublishForm((prev) => ({ ...prev, version: event.target.value }))
                  }
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">构建号</label>
                <Input
                  type="number"
                  min={1}
                  value={publishForm.buildNumber}
                  placeholder="例如 42"
                  onChange={(event) =>
                    setPublishForm((prev) => ({ ...prev, buildNumber: event.target.value }))
                  }
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">更新说明</label>
              <Textarea
                rows={4}
                value={publishForm.releaseNotes}
                placeholder="填写本次更新的亮点或修复内容"
                onChange={(event) =>
                  setPublishForm((prev) => ({ ...prev, releaseNotes: event.target.value }))
                }
              />
            </div>
            <Button type="submit" className="w-full" disabled={createMutation.isPending}>
              {createMutation.isPending ? '发布中...' : '发布新版本'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-gray-100 shadow-md">
        <CardHeader className="bg-gray-50 border-b border-gray-100">
          <CardTitle className="text-lg font-bold">发布记录</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {releasesQuery.isLoading ? (
            <p className="text-sm text-gray-500">正在加载...</p>
          ) : (
            renderReleaseList(releasesQuery.data)
          )}
        </CardContent>
      </Card>

      <Dialog open={editDialog.open} onOpenChange={(open) => {
        if (!open && !editMutation.isPending) {
          setEditDialog({
            open: false,
            release: null,
            version: '',
            buildNumber: '',
            releaseNotes: '',
          })
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>编辑版本信息</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleEditSubmit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="release-version">版本号</Label>
                <Input
                  id="release-version"
                  value={editDialog.version}
                  onChange={(event) =>
                    setEditDialog((prev) => ({ ...prev, version: event.target.value }))
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="release-build">构建号</Label>
                <Input
                  id="release-build"
                  type="number"
                  min={1}
                  value={editDialog.buildNumber}
                  onChange={(event) =>
                    setEditDialog((prev) => ({ ...prev, buildNumber: event.target.value }))
                  }
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="release-notes">更新说明</Label>
              <Textarea
                id="release-notes"
                rows={4}
                value={editDialog.releaseNotes}
                onChange={(event) =>
                  setEditDialog((prev) => ({ ...prev, releaseNotes: event.target.value }))
                }
              />
            </div>
            <DialogFooter className="gap-2 sm:space-x-2">
              <Button type="button" variant="ghost" onClick={() => setEditDialog({
                open: false,
                release: null,
                version: '',
                buildNumber: '',
                releaseNotes: '',
              })} disabled={editMutation.isPending}>
                取消
              </Button>
              <Button type="submit" disabled={editMutation.isPending}>
                {editMutation.isPending ? '保存中...' : '保存修改'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialog.open} onOpenChange={(open) => {
        if (!open && !deleteMutation.isPending) {
          setDeleteDialog({ open: false, release: null })
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>确认删除版本</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            确定要删除版本
            <span className="font-semibold text-gray-900">
              {' '}
              v{deleteDialog.release?.version} (Build {deleteDialog.release?.buildNumber})
            </span>
            吗？此操作不可撤销。
          </p>
          <DialogFooter className="gap-2 sm:space-x-2">
            <Button type="button" variant="ghost" onClick={() => setDeleteDialog({ open: false, release: null })} disabled={deleteMutation.isPending}>
              取消
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={() => deleteDialog.release && deleteMutation.mutate(deleteDialog.release.buildNumber)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? '删除中...' : '确认删除'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
